import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import AppTooltip from '../../../components/ui/AppTooltip';
import { isFeedFeatureEnabled } from '../../../utils/featureFlags';
import { getTraineeGamificationData } from '../../../services/organization/trainees/trainerAnalytics.service';
import Badges from '../../../assets/images/dashboard/badge.png';

const isFeedBadge = (badge) => {
  const criteria = badge?.criteria_json;
  const criteriaText = typeof criteria === 'string' ? criteria : JSON.stringify(criteria || {});
  return criteriaText.includes('feed_posts');
};

const formatNumber = (value) => (Number(value) || 0).toLocaleString();

function InfoTooltip({ label, description }) {
  return (
    <AppTooltip content={<div className="tw-flex tw-flex-col tw-gap-1"><strong>{label}</strong><span>{description}</span></div>}>
      <button
        type="button"
        aria-label={`About ${label.toLowerCase()}`}
        className="tw-inline-flex tw-size-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-slate-500 hover:tw-bg-white/70 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-brand-600"
      >
        <Icon icon="fluent:info-24-regular" width="16" height="16" />
      </button>
    </AppTooltip>
  );
}

function TraineeAnalyticsGamification({ traineeId, dashboardStats = {} }) {
  const [gamificationData, setGamificationData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!traineeId) {
      setGamificationData(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setGamificationData(null);

    const fetchGamificationData = async () => {
      try {
        const traineeResult = await getTraineeGamificationData(traineeId);
        if (active && traineeResult?.success) {
          setGamificationData(traineeResult.data);
        }
      } catch {
        if (active) setGamificationData(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchGamificationData();
    return () => { active = false; };
  }, [traineeId]);

  if (loading) {
    return <div className="tw-flex tw-min-h-[200px] tw-items-center tw-justify-center"><Loader /></div>;
  }

  if (!gamificationData) {
    return <NoData message="No gamification data available" description="This trainee has no gamification record yet." />;
  }

  const userData = gamificationData.userData || {};
  const levels = Array.isArray(gamificationData.levels) ? gamificationData.levels : [];
  const allBadges = Array.isArray(gamificationData.allBadges) ? gamificationData.allBadges : [];
  const earnedBadges = gamificationData.userBadges?.earned_badges || [];
  const visibleBadges = isFeedFeatureEnabled() ? allBadges : allBadges.filter((badge) => !isFeedBadge(badge));
  const earnedBadgeIds = new Set(earnedBadges.map((badge) => Number(badge.badge_id)));
  const earnedVisibleCount = visibleBadges.filter((badge) => earnedBadgeIds.has(Number(badge.id))).length;
  const currentLevel = Number(userData.current_level) || 1;
  const learningCards = [
    { title: 'Courses', value: dashboardStats.total_enrolled_courses ?? (Number(dashboardStats.active_courses || 0) + Number(dashboardStats.completed_courses || 0)), icon: 'fluent:book-24-regular', background: '#e0f2f1', iconColor: '#009688' },
    { title: 'Classrooms', value: dashboardStats.active_classrooms, icon: 'fluent:home-24-regular', background: '#f8e5ff', iconColor: '#9c27b0' },
    { title: 'Certificates', value: dashboardStats.certificates, icon: 'fluent:document-24-regular', background: '#e3f2fd', iconColor: '#2196f3' },
    { title: 'Free Courses', value: dashboardStats.free_courses, icon: 'fluent:gift-24-regular', background: '#e8f5e9', iconColor: '#4caf50' },
    { title: 'Paid Courses', value: dashboardStats.paid_courses, icon: 'fluent:premium-24-regular', background: '#fce4ec', iconColor: '#e91e63' },
    { title: 'Completed', value: dashboardStats.completed_courses, icon: 'fluent:task-list-square-24-regular', background: '#ede7f6', iconColor: '#673ab7' },
  ];
  const statCards = [
    {
      title: 'Current Points',
      value: formatNumber(userData.total_points),
      icon: 'fluent:star-24-regular',
      background: '#fff3e0',
      iconColor: '#ff9800',
      description: 'Earn points by completing assessments, assignments, videos, and other activities. Points help trainees level up and unlock achievements.',
    },
    {
      title: 'Current Streak',
      value: formatNumber(userData.current_streak),
      icon: 'fluent:fire-24-regular',
      background: '#fce4ec',
      iconColor: '#e91e63',
      description: 'Logging in once within 24 hours counts as a day. A longer streak can earn bonus points and rewards.',
      detail: `Longest: ${formatNumber(userData.longest_streak)}`,
    },
    {
      title: 'Current Level',
      value: currentLevel,
      icon: 'fluent:target-24-regular',
      background: '#e8f4fd',
      iconColor: '#1976d2',
      description: 'Achievement level is based on points and badges. Higher levels unlock new features and privileges.',
    },
  ];

  return (
    <div className="tw-w-full">
      <section className="ui-card tw-mb-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <header className="tw-border-b tw-border-slate-100 tw-px-5 tw-py-4">
          <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-slate-900">Learning Overview</h3>
        </header>
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 tw-p-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          {learningCards.map((card) => (
            <div key={card.title} className="ui-card tw-flex tw-min-h-[84px] tw-min-w-0 tw-items-center tw-rounded-xl tw-p-3 tw-shadow-sm" style={{ backgroundColor: card.background }}>
              <div className="tw-mr-3 tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
                <Icon icon={card.icon} width="22" height="22" style={{ color: card.iconColor }} />
              </div>
              <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-slate-500">{card.title}</div>
                <div className="tw-text-xl tw-font-bold tw-text-slate-900">{formatNumber(card.value)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 tw-px-4 tw-pb-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          {statCards.map((card) => (
            <div key={card.title} className="ui-card tw-relative tw-flex tw-min-h-[110px] tw-items-center tw-rounded-xl tw-p-4 tw-shadow-sm" style={{ backgroundColor: card.background }}>
              <div className="tw-mr-4 tw-flex tw-size-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
                <Icon icon={card.icon} width="24" height="24" style={{ color: card.iconColor }} />
              </div>
              <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-slate-600">{card.title}</div>
                <div className="tw-text-2xl tw-font-bold tw-text-slate-900">{card.value}</div>
              </div>
              {card.detail && <span className="tw-absolute tw-bottom-2 tw-right-2 tw-rounded-full tw-bg-white/70 tw-px-2 tw-py-0.5 tw-text-xs tw-text-slate-600">{card.detail}</span>}
              <div className="tw-absolute tw-right-2 tw-top-2">
                <InfoTooltip label={card.title} description={card.description} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="ui-card tw-mb-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <header className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-100 tw-px-5 tw-py-4">
          <h3 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-slate-900"><Icon icon="fluent:target-24-regular" width="20" height="20" className="tw-text-brand-600" />All Levels</h3>
          <InfoTooltip label="Level Requirements" description="Earn the required points, badges, and streak to reach the next level." />
        </header>
        {levels.length ? (
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-4 md:tw-grid-cols-3 xl:tw-grid-cols-5">
            {levels.map((level) => {
              const isCurrent = Number(level.level) === currentLevel;
              return (
                <div key={level.id} className={`tw-flex tw-min-w-0 tw-flex-col tw-items-center tw-rounded-xl tw-border tw-p-3 tw-text-center tw-shadow-sm ${isCurrent ? 'tw-border-brand-600 tw-bg-indigo-50' : 'tw-border-slate-200 tw-bg-white'}`}>
                  <div className={`tw-mb-2 tw-flex tw-size-11 tw-items-center tw-justify-center tw-rounded-full ${isCurrent ? 'tw-bg-brand-600 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-500'}`}>
                    <Icon icon="fluent:target-24-regular" width="22" height="22" />
                  </div>
                  <div className={`tw-text-sm tw-font-semibold ${isCurrent ? 'tw-text-brand-600' : 'tw-text-slate-900'}`}>Level {level.level}</div>
                  <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-slate-700">{level.name}</div>
                  <div className="tw-mt-auto tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-slate-500">
                    <span>{formatNumber(level.min_points)} pts</span>
                    {Number(level.min_badges) > 0 && <span>{level.min_badges} badges</span>}
                    {Number(level.longest_streak) > 0 && <span>{level.longest_streak} streak</span>}
                  </div>
                  {isCurrent && <span className="tw-mt-2 tw-rounded-full tw-border tw-border-brand-600 tw-bg-indigo-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-brand-600">Current Level</span>}
                </div>
              );
            })}
          </div>
        ) : <NoData message="No levels available" />}
      </section>

      <section className="ui-card tw-mb-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-slate-100 tw-px-5 tw-py-4">
          <h3 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-slate-900"><Icon icon="fluent:trophy-24-regular" width="20" height="20" className="tw-text-brand-600" />All Badges</h3>
          <span className="tw-rounded-full tw-bg-indigo-50 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-brand-600">{earnedVisibleCount} of {visibleBadges.length} earned</span>
        </header>
        {visibleBadges.length ? (
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-4 md:tw-grid-cols-3 xl:tw-grid-cols-5">
            {visibleBadges.map((badge) => {
              const isEarned = earnedBadgeIds.has(Number(badge.id));
              return (
                <div key={badge.id} className={`tw-relative tw-flex tw-h-[112px] tw-min-w-0 tw-flex-col tw-items-center tw-rounded-xl tw-border tw-p-2 tw-text-center tw-shadow-sm ${isEarned ? 'tw-border-emerald-300 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-white'}`}>
                  {badge.description && (
                    <div className="tw-absolute tw-right-2 tw-top-2">
                      <InfoTooltip label={badge.badges_name} description={badge.description} />
                    </div>
                  )}
                  <div className="tw-mb-1 tw-flex tw-size-8 tw-items-center tw-justify-center tw-text-slate-500">
                    {badge.icon_url ? <img src={Badges} alt="" className="tw-size-8 tw-object-contain" /> : <Icon icon="fluent:trophy-24-regular" width="28" height="28" />}
                  </div>
                  <div className="tw-w-full tw-truncate tw-text-xs tw-font-semibold tw-text-slate-700" title={badge.badges_name}>{String(badge.badges_name || '').replace(' Badge', '')}</div>
                  <span className={`tw-mt-auto tw-rounded-full tw-px-2 tw-py-1 tw-text-[0.7rem] tw-font-semibold ${isEarned ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-slate-200 tw-text-slate-600'}`}>{isEarned ? 'Earned' : 'Not earned'}</span>
                </div>
              );
            })}
          </div>
        ) : <NoData message="No badges available" />}
      </section>

    </div>
  );
}

export default TraineeAnalyticsGamification;
